import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type {
  PageIndex,
  CreatePageIndexRequest,
  UpdatePageIndexRequest,
  UpdatePrintBatchRequest,
  PageIndexListParams,
  StickingGlobalMetrics,
  PrintBatch,
  CreatePrintBatchRequest,
  PrintBatchListParams,
  PrintBatchMetrics,
  SetPageCountRequest,
  RequestExtraStickingLabelsRequest,
  AdjustActualPageCountRequest
} from '../types';
import type { Registration } from '@/features/registration';

const { INDEXING, REGISTRATIONS } = API_ENDPOINTS;

export const stickingApi = {
  getPageIndexes: (registrationId: number, params?: PageIndexListParams) =>
    apiClient.getPaginated<PageIndex>(
      INDEXING.PAGE_INDEXES(registrationId),
      params
    ),

  getPageIndex: (registrationId: number, pageIndexId: number) =>
    apiClient.get<PageIndex>(INDEXING.PAGE_INDEX(registrationId, pageIndexId)),

  createPageIndex: (registrationId: number, data: CreatePageIndexRequest) =>
    apiClient.post<PageIndex>(INDEXING.PAGE_INDEXES(registrationId), data),

  updatePageIndex: (
    registrationId: number,
    pageIndexId: number,
    data: UpdatePageIndexRequest
  ) =>
    apiClient.put<PageIndex>(
      INDEXING.PAGE_INDEX(registrationId, pageIndexId),
      data
    ),

  deletePageIndex: (registrationId: number, pageIndexId: number) =>
    apiClient.delete(INDEXING.PAGE_INDEX(registrationId, pageIndexId)),

  bulkDeletePageIndexes: (registrationId: number, ids: number[]) =>
    apiClient.post(INDEXING.BULK_DELETE_PAGE_INDEXES(registrationId), { ids }),

  globalMetrics: () => apiClient.get<StickingGlobalMetrics>(INDEXING.METRICS),

  // Print batch endpoints
  getPrintBatches: (registrationId: number, params?: PrintBatchListParams) =>
    apiClient.getPaginated<PrintBatch>(
      INDEXING.PRINT_BATCHES(registrationId),
      params
    ),

  createPrintBatch: (registrationId: number, data: CreatePrintBatchRequest) =>
    apiClient.post<PrintBatch>(INDEXING.PRINT_BATCHES(registrationId), data),

  updatePrintBatch: (
    registrationId: number,
    batchId: number,
    data: UpdatePrintBatchRequest
  ) =>
    apiClient.put<PrintBatch>(
      INDEXING.PRINT_BATCH(registrationId, batchId),
      data
    ),

  getPrintBatchMetrics: (registrationId: number) =>
    apiClient.get<PrintBatchMetrics>(
      INDEXING.PRINT_BATCH_METRICS(registrationId)
    ),

  // Sticking-stage workflow actions
  setPageCount: (registrationId: number, payload: SetPageCountRequest) =>
    apiClient.patch<Registration>(
      REGISTRATIONS.SET_PAGE_COUNT(registrationId),
      payload
    ),

  requestExtraStickingLabels: (
    registrationId: number,
    payload: RequestExtraStickingLabelsRequest
  ) =>
    apiClient.post<PrintBatch>(
      REGISTRATIONS.STICKING_REQUEST_EXTRA_LABELS(registrationId),
      payload
    ),

  adjustActualPageCount: (
    registrationId: number,
    payload: AdjustActualPageCountRequest
  ) =>
    apiClient.patch<Registration>(
      REGISTRATIONS.STICKING_ADJUST_ACTUAL_COUNT(registrationId),
      payload
    ),

  returnToRegistration: (registrationId: number) =>
    apiClient.post<Registration>(
      REGISTRATIONS.RETURN_TO_REGISTRATION(registrationId)
    )
};
